'use client'
import type { BoxProps } from '@mui/material/Box'
import type { CheckboxProps } from '@mui/material/Checkbox'
import type { FormControlProps } from '@mui/material/FormControl'
import type { FormControlLabelProps } from '@mui/material/FormControlLabel'
import type { FormGroupProps } from '@mui/material/FormGroup'
import type { FormHelperTextProps } from '@mui/material/FormHelperText'
import type { FormLabelProps } from '@mui/material/FormLabel'

import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import FormLabel from '@mui/material/FormLabel'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { HelperText } from '@/components/form'
// ----------------------------------------------------------------------

type TCheckboxProps = Omit<FormControlLabelProps, 'control'> & {
  helperText?: React.ReactNode
  slotProps?: {
    wrapper?: BoxProps
    checkbox?: CheckboxProps
    helperText?: FormHelperTextProps
  }
}

export function CheckboxCT({ sx, name, label, slotProps, helperText, ...other }: TCheckboxProps) {
  return (
    <Box
      {...slotProps?.wrapper}
      sx={{
        ...slotProps?.wrapper?.sx,
      }}
    >
      <FormControlLabel
        label={label}
        control={
          <Checkbox
            {...slotProps?.checkbox}
            sx={{
              p: 0,
              mr: label ? 1 : 0,
            }}
            icon={
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  border: (theme) => `solid 1px ${theme.palette.grey[500]}`,
                }}
              />
            }
            checkedIcon={
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'primary.main',
                  border: (theme) => `solid 1px ${theme.palette.primary.main}`,
                }}
              >
                <CheckRoundedIcon sx={{ color: '#fff', fontSize: '13px', fill: 'currentColor' }} />
              </Box>
            }
          />
        }
        sx={[
          {
            mx: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: { xs: '12px', md: '14px' }, // Responsive: Mobile 14px, Desktop 16px
            },
          },
          ...(Array.isArray(sx) ? (sx ?? []) : [sx]),
        ]}
        {...other}
      />
      <Box position="absolute" sx={{ left: '8px', top: '80%', width: '100%' }}>
        <HelperText
          {...slotProps?.helperText}
          sx={{
            color: 'error.main',
            ...slotProps?.helperText?.sx,
          }}
          helperText={helperText}
        />
      </Box>
    </Box>
  )
}

// ----------------------------------------------------------------------

type RHFMultiCheckboxProps = FormGroupProps & {
  name: string
  label?: string
  helperText?: React.ReactNode
  options: { label: string; value: string }[]
  slotProps?: {
    wrapper?: FormControlProps
    checkbox?: CheckboxProps
    formLabel?: FormLabelProps
    helperText?: FormHelperTextProps
  }
}

export function MultiCheckboxCT({
  name,
  label,
  options,
  slotProps,
  helperText,
  ...other
}: RHFMultiCheckboxProps) {
  const getSelected = (selectedItems: string[], item: string) =>
    selectedItems.includes(item) ? selectedItems.filter((value) => value !== item) : [...selectedItems, item]

  return (
    <FormControl component="fieldset" {...slotProps?.wrapper}>
      {label && (
        <FormLabel
          component="legend"
          {...slotProps?.formLabel}
          sx={[
            { mb: 1, typography: 'body2' },
            ...(Array.isArray(slotProps?.formLabel?.sx)
              ? (slotProps?.formLabel?.sx ?? [])
              : [slotProps?.formLabel?.sx]),
          ]}
        >
          {label}
        </FormLabel>
      )}

      <FormGroup {...other}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                {...slotProps?.checkbox}
                inputProps={{
                  id: `${option.label}-checkbox`,
                  ...(!option.label && { 'aria-label': `${option.label} checkbox` }),
                  ...slotProps?.checkbox?.inputProps,
                }}
              />
            }
            label={option.label}
          />
        ))}
      </FormGroup>
    </FormControl>
  )
}
